// The console's site socket (PMU-202): one per console, in the shell, so every page redraws on a
// change without a reload. Messages only say that something changed; the pages refetch through
// the cache, which revalidates the paths already on screen.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useRefresh } from "./data.tsx";
import { emitModuleLive } from "../shared/live.ts";

type Live = "connecting" | "live" | "reconnecting" | "off";

/** Site WebSocket with exponential backoff (1 s doubling to 30 s, with jitter) and a keepalive ping. */
function useLive(base: string, onMessage: (m: { type: string }) => void): Live {
  const [state, setState] = useState<Live>("connecting");
  const cb = useRef(onMessage);
  cb.current = onMessage;
  useEffect(() => {
    if (typeof WebSocket === "undefined") { setState("off"); return; }
    let ws: WebSocket | null = null; let tries = 0; let timer: ReturnType<typeof setTimeout> | undefined; let ping: ReturnType<typeof setInterval> | undefined; let closed = false;
    const connect = () => {
      ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${base}/ws`);
      ws.onopen = () => { tries = 0; setState("live"); ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25_000); };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const m = JSON.parse(String(e.data));
          // A module's own message (a new chat message) goes to that module, not to the board.
          if (m.module) emitModuleLive(m);
          else if (m.type === "event" || m.type === "request") cb.current(m);
        } catch { /* not json */ }
      };
      ws.onclose = () => {
        clearInterval(ping);
        if (closed) return;
        setState("reconnecting");
        const delay = Math.min(30_000, 1000 * 2 ** tries++) * (0.75 + Math.random() * 0.5);
        timer = setTimeout(() => { cb.current({ type: "resync" }); connect(); }, delay);
      };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); clearInterval(ping); ws?.close(); };
  }, [base]);
  return state;
}


const LiveContext = createContext<Live>("off");
export const useLiveState = () => useContext(LiveContext);

/** Paths a change can touch; only those already fetched (on screen or recently seen) refetch. */
const touched = (base: string) => [`${base}/board`, `${base}/requests`, `${base}/pickup-requests`, `${base}/events`, `${base}/children`];

export function ConsoleLive({ base, children }: { base: string; children: ReactNode }) {
  const refresh = useRefresh();
  const pending = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Coalesce bursts (a sibling group is several events) into one refetch.
  const bump = () => { clearTimeout(pending.current); pending.current = setTimeout(() => refresh(...touched(base)), 300); };
  const state = useLive(base, bump);
  // Back to the tab: whatever happened meanwhile.
  useEffect(() => {
    const wake = () => { if (document.visibilityState === "visible") bump(); };
    document.addEventListener("visibilitychange", wake);
    return () => { document.removeEventListener("visibilitychange", wake); clearTimeout(pending.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);
  return <LiveContext.Provider value={state}>{children}</LiveContext.Provider>;
}
